import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  Loader2,
  Sparkles,
  RefreshCw,
  Trash2,
  Eye,
  Pencil,
  Globe,
  FileText,
  X,
  MapPin,
  Plus,
  Save,
  SlidersHorizontal,
  Tag,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "softpro_admin_token";
const authHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` },
});

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("nl-NL", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return "";
  }
};

const EditModal = ({ id, onClose, onSaved }) => {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/admin/articles/${id}`, authHeader())
      .then(({ data }) => setData(data))
      .catch(() => {
        toast.error("Laden mislukt");
        onClose();
      });
  }, [id, onClose]);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(
        `${API}/admin/articles/${id}`,
        {
          title: data.title,
          meta_title: data.meta_title,
          meta_description: data.meta_description,
          excerpt: data.excerpt,
          content_html: data.content_html,
          cover_image: data.cover_image,
        },
        authHeader()
      );
      toast.success("Opgeslagen");
      onSaved();
      onClose();
    } catch {
      toast.error("Opslaan mislukt");
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    "w-full rounded-2xl border-2 border-slate-200 bg-white px-4 py-3 text-ink outline-none focus:border-navy";

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-ink/70 p-3 sm:p-6"
      onClick={onClose}
      data-testid="blog-edit-modal"
    >
      <div
        className="flex max-h-[88vh] w-full max-w-2xl flex-col overflow-hidden rounded-3xl bg-white"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <h3 className="font-display text-lg font-bold text-ink">Artikel bewerken</h3>
          <button onClick={onClose} className="grid h-9 w-9 place-items-center rounded-full text-slate-400 hover:bg-secondary">
            <X className="h-5 w-5" />
          </button>
        </div>
        {!data ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-7 w-7 animate-spin text-navy" />
          </div>
        ) : (
          <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
            <div>
              <label className="text-sm font-semibold text-ink">Titel (H1)</label>
              <input value={data.title || ""} onChange={(e) => set("title", e.target.value)} className={`mt-1 ${inputCls}`} data-testid="edit-title" />
            </div>
            <div>
              <label className="text-sm font-semibold text-ink">SEO titel</label>
              <input value={data.meta_title || ""} onChange={(e) => set("meta_title", e.target.value)} className={`mt-1 ${inputCls}`} />
            </div>
            <div>
              <label className="text-sm font-semibold text-ink">Meta description</label>
              <textarea rows={2} value={data.meta_description || ""} onChange={(e) => set("meta_description", e.target.value)} className={`mt-1 resize-none ${inputCls}`} />
            </div>
            <div>
              <label className="text-sm font-semibold text-ink">Samenvatting (excerpt)</label>
              <textarea rows={2} value={data.excerpt || ""} onChange={(e) => set("excerpt", e.target.value)} className={`mt-1 resize-none ${inputCls}`} />
            </div>
            <div>
              <label className="text-sm font-semibold text-ink">Omslagafbeelding (URL)</label>
              <input value={data.cover_image || ""} onChange={(e) => set("cover_image", e.target.value)} className={`mt-1 ${inputCls}`} />
            </div>
            <div>
              <label className="text-sm font-semibold text-ink">Inhoud (HTML)</label>
              <textarea rows={14} value={data.content_html || ""} onChange={(e) => set("content_html", e.target.value)} className={`mt-1 font-mono text-xs ${inputCls}`} data-testid="edit-content" />
            </div>
          </div>
        )}
        <div className="border-t border-slate-100 px-6 py-4">
          <button
            onClick={save}
            disabled={saving || !data}
            data-testid="edit-save"
            className="flex w-full items-center justify-center gap-2 rounded-full bg-navy px-6 py-3.5 font-semibold text-white disabled:opacity-60"
          >
            {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : "Opslaan"}
          </button>
        </div>
      </div>
    </div>
  );
};

export const BlogPanel = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [editId, setEditId] = useState(null);
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [topics, setTopics] = useState([]);
  const [newKeyword, setNewKeyword] = useState("");
  const [genTopics, setGenTopics] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, s, t] = await Promise.all([
        axios.get(`${API}/admin/articles`, authHeader()),
        axios.get(`${API}/admin/blog-settings`, authHeader()),
        axios.get(`${API}/admin/topics`, authHeader()),
      ]);
      setArticles(a.data);
      setSettings(s.data);
      setTopics(t.data);
    } catch {
      toast.error("Laden mislukt");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const saveSettings = async (patch) => {
    const next = { ...settings, ...patch };
    setSettings(next);
    setSavingSettings(true);
    try {
      await axios.put(`${API}/admin/blog-settings`, patch, authHeader());
      toast.success("Instellingen opgeslagen");
    } catch {
      toast.error("Opslaan mislukt");
    } finally {
      setSavingSettings(false);
    }
  };

  const addTopic = async () => {
    const kw = newKeyword.trim();
    if (!kw) return;
    try {
      await axios.post(`${API}/admin/topics`, { keyword: kw, title: kw }, authHeader());
      setNewKeyword("");
      toast.success("Onderwerp toegevoegd");
      load();
    } catch {
      toast.error("Toevoegen mislukt");
    }
  };

  const deleteTopic = async (id) => {
    try {
      await axios.delete(`${API}/admin/topics/${id}`, authHeader());
      load();
    } catch {
      toast.error("Verwijderen mislukt");
    }
  };

  const generateTopics = async () => {
    setGenTopics(true);
    try {
      const { data } = await axios.post(`${API}/admin/topics/generate`, {}, authHeader());
      toast.success(`${data.count} AI-onderwerpen toegevoegd`);
      load();
    } catch {
      toast.error("Genereren mislukt");
    } finally {
      setGenTopics(false);
    }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      await axios.post(`${API}/admin/articles/generate`, {}, authHeader());
      toast.success("Concept wordt gegenereerd (~30-60s). De lijst wordt zo ververst.");
      setTimeout(() => load().then(() => setGenerating(false)), 50000);
    } catch {
      toast.error("Genereren mislukt");
      setGenerating(false);
    }
  };

  const togglePublish = async (a) => {
    try {
      await axios.post(
        `${API}/admin/articles/${a.id}/publish`,
        { published: a.status !== "published" },
        authHeader()
      );
      toast.success(a.status === "published" ? "Teruggezet naar concept" : "Gepubliceerd");
      load();
    } catch {
      toast.error("Actie mislukt");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Dit artikel verwijderen?")) return;
    try {
      await axios.delete(`${API}/admin/articles/${id}`, authHeader());
      toast.success("Verwijderd");
      load();
    } catch {
      toast.error("Verwijderen mislukt");
    }
  };

  return (
    <div data-testid="blog-panel">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold text-ink">Blog</h1>
          <p className="mt-1 text-muted-foreground">
            AI-concepten (2×/week automatisch). Controleer, bewerk en publiceer met één klik.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={load}
            className="grid h-11 w-11 place-items-center rounded-full border border-border text-ink hover:bg-secondary"
            aria-label="Vernieuwen"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
          <button
            onClick={generate}
            disabled={generating}
            data-testid="blog-generate"
            className="inline-flex items-center gap-2 rounded-full bg-teal px-5 py-2.5 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5 disabled:opacity-60"
          >
            {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {generating ? "Bezig..." : "Genereer concept"}
          </button>
        </div>
      </div>

      {/* Settings */}
      {settings && (
        <div className="mt-6 rounded-3xl border border-border bg-white p-5" data-testid="blog-settings">
          <div className="flex items-center gap-2">
            <SlidersHorizontal className="h-5 w-5 text-navy" />
            <h2 className="font-display text-lg font-bold text-ink">Publicatie-instellingen</h2>
            {savingSettings && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
          </div>
          <div className="mt-4 grid gap-4 sm:grid-cols-3">
            <div>
              <label className="text-sm font-semibold text-ink">Hoe vaak publiceren</label>
              <select
                value={settings.frequency}
                onChange={(e) => saveSettings({ frequency: e.target.value })}
                data-testid="setting-frequency"
                className="mt-1.5 w-full rounded-2xl border-2 border-slate-200 bg-white px-4 py-3 text-ink outline-none focus:border-navy"
              >
                <option value="daily">Dagelijks</option>
                <option value="3x_week">3× per week (ma/wo/vr)</option>
                <option value="2x_week">2× per week (ma/do)</option>
                <option value="weekly">Wekelijks (ma)</option>
              </select>
            </div>
            <label className="flex cursor-pointer items-start gap-3 rounded-2xl border-2 border-slate-200 p-3.5">
              <input
                type="checkbox"
                checked={!!settings.auto_generate_enabled}
                onChange={(e) => saveSettings({ auto_generate_enabled: e.target.checked })}
                data-testid="setting-autogen"
                className="mt-0.5 h-5 w-5 accent-navy"
              />
              <span>
                <span className="block text-sm font-semibold text-ink">Automatisch genereren</span>
                <span className="block text-xs text-muted-foreground">AI maakt concepten op schema</span>
              </span>
            </label>
            <label className="flex cursor-pointer items-start gap-3 rounded-2xl border-2 border-slate-200 p-3.5">
              <input
                type="checkbox"
                checked={!!settings.auto_publish}
                onChange={(e) => saveSettings({ auto_publish: e.target.checked })}
                data-testid="setting-autopublish"
                className="mt-0.5 h-5 w-5 accent-teal"
              />
              <span>
                <span className="block text-sm font-semibold text-ink">Direct publiceren</span>
                <span className="block text-xs text-muted-foreground">Zonder handmatige goedkeuring live</span>
              </span>
            </label>
          </div>
        </div>
      )}

      {/* Topics */}
      <div className="mt-4 rounded-3xl border border-border bg-white p-5" data-testid="blog-topics">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Tag className="h-5 w-5 text-navy" />
            <h2 className="font-display text-lg font-bold text-ink">Onderwerpen</h2>
            <span className="text-xs text-muted-foreground">({topics.filter((t) => !t.used).length} open)</span>
          </div>
          <button
            onClick={generateTopics}
            disabled={genTopics}
            data-testid="topics-generate"
            className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-semibold text-ink hover:bg-secondary disabled:opacity-60"
          >
            {genTopics ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4 text-teal" />}
            AI-suggesties
          </button>
        </div>
        <div className="mt-4 flex gap-2">
          <input
            value={newKeyword}
            onChange={(e) => setNewKeyword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addTopic()}
            placeholder="Eigen zoekwoord of onderwerp toevoegen…"
            data-testid="topic-input"
            className="w-full rounded-2xl border-2 border-slate-200 bg-white px-4 py-3 text-ink outline-none focus:border-navy"
          />
          <button
            onClick={addTopic}
            data-testid="topic-add"
            className="inline-flex shrink-0 items-center gap-1.5 rounded-2xl bg-navy px-5 py-3 text-sm font-semibold text-white"
          >
            <Plus className="h-4 w-4" /> Toevoegen
          </button>
        </div>
        {topics.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {topics.map((t) => (
              <span
                key={t.id || t.keyword}
                data-testid={`topic-${t.id || t.keyword}`}
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium ${
                  t.used ? "bg-secondary text-slate-400 line-through" : "bg-navy/10 text-navy"
                }`}
              >
                {t.keyword}
                {!t.used && t.id && (
                  <button onClick={() => deleteTopic(t.id)} className="text-navy/60 hover:text-red-500" aria-label="Verwijderen">
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="mt-6 space-y-3" data-testid="blog-list">
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-navy" />
          </div>
        ) : articles.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-border bg-white py-16 text-center text-muted-foreground">
            Nog geen artikelen. Klik op "Genereer concept" om te starten.
          </div>
        ) : (
          articles.map((a) => (
            <div
              key={a.id}
              data-testid={`blog-item-${a.id}`}
              className="flex flex-col gap-4 rounded-3xl border border-border bg-white p-4 sm:flex-row sm:items-center"
            >
              <img src={a.cover_image} alt={a.title} className="h-20 w-full rounded-2xl object-cover sm:w-28" />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                      a.status === "published"
                        ? "bg-emerald-100 text-emerald-700"
                        : "bg-amber-100 text-amber-700"
                    }`}
                  >
                    {a.status === "published" ? "Gepubliceerd" : "Concept"}
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-2.5 py-0.5 text-xs font-semibold text-ink">
                    {a.category === "Lokale SEO" && <MapPin className="h-3 w-3" />}
                    {a.category}
                  </span>
                  <span className="text-xs text-muted-foreground">{fmtDate(a.created_at)}</span>
                </div>
                <h3 className="mt-1.5 truncate font-display text-base font-bold text-ink">{a.title}</h3>
                <p className="truncate text-sm text-muted-foreground">{a.excerpt}</p>
              </div>
              <div className="flex flex-wrap gap-2 sm:justify-end">
                {a.status === "published" && (
                  <a
                    href={`/blog/${a.slug}`}
                    target="_blank"
                    rel="noreferrer"
                    className="grid h-10 w-10 place-items-center rounded-full border border-border text-ink hover:bg-secondary"
                    aria-label="Bekijken"
                  >
                    <Eye className="h-4 w-4" />
                  </a>
                )}
                <button
                  onClick={() => setEditId(a.id)}
                  data-testid={`blog-edit-${a.id}`}
                  className="grid h-10 w-10 place-items-center rounded-full border border-border text-ink hover:bg-secondary"
                  aria-label="Bewerken"
                >
                  <Pencil className="h-4 w-4" />
                </button>
                <button
                  onClick={() => togglePublish(a)}
                  data-testid={`blog-publish-${a.id}`}
                  className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2.5 text-sm font-semibold ${
                    a.status === "published"
                      ? "bg-secondary text-ink hover:bg-slate-200"
                      : "bg-navy text-white hover:-translate-y-0.5 transition-transform"
                  }`}
                >
                  {a.status === "published" ? <FileText className="h-4 w-4" /> : <Globe className="h-4 w-4" />}
                  {a.status === "published" ? "Concept" : "Publiceer"}
                </button>
                <button
                  onClick={() => remove(a.id)}
                  data-testid={`blog-delete-${a.id}`}
                  className="grid h-10 w-10 place-items-center rounded-full border border-border text-red-500 hover:bg-red-50"
                  aria-label="Verwijderen"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {editId && <EditModal id={editId} onClose={() => setEditId(null)} onSaved={load} />}
    </div>
  );
};

export default BlogPanel;
